import axios from 'axios'
import {
  CreditCardOrders,
  DeveloperChartView,
  FinalUnitPrice,
  IntegrationProjects,
  IntegrationPurchase,
  IntegrationStatics,
  MainIntegrationChart,
  MyIntegrationsType,
  OffsetOptionalFieldsType,
  PrePurchaseBuyNow,
  SmallProjectIntegration,
  SmallWhiteLabel
} from 'src/core/models'

export async function createIntegrationAPI(payload: any): Promise<{ data: any }> {
  const { data } = await axios.post('/api/v1/widget/create_business/', payload)
  return data
}

export async function getUserIntegrationsListAPI(currency_code: string): Promise<MyIntegrationsType[]> {
  const params = `?currency_code=${currency_code}`
  const url = `/api/v1/user_integrations/get_all_user_integrations_for_account/${params}`
  const { data } = await axios.get(url)
  return data.data?.integration_list || data.data
}

export async function getMainIntegrationChartDataAPI(
  currency_code: string, chartView: DeveloperChartView
): Promise<MainIntegrationChart> {
  const params = `?currency_code=${currency_code}&chart_view=${chartView}`
  const url = `/api/v1/user_integrations/get_all_integrations_purchase_chart_data/`
  const { data } = await axios.get(`${url}${params}`)
  return data.data
}

export async function getIntegrationPurchasesListAPI(
  id: number, currency_code: string
): Promise<IntegrationPurchase[]> {
  const params = `?currency_code=${currency_code}`
  const url = `/api/v1/user_integrations/${id}/get_integration_purchases/`
  const { data } = await axios.get(`${url}${params}`)
  return data.data
}

export async function getIntegrationChartDataAPI(
  id: number,
  currency_code: string,
  chartView: DeveloperChartView
): Promise<MainIntegrationChart> {
  const params = `?currency_code=${currency_code}&chart_view=${chartView}`
  const url = `/api/v1/user_integrations/${id}/get_integration_purchase_chart_data/`
  const { data } = await axios.get(`${url}${params}`)
  return data.data
}

export async function getIntegrationStaticsAPI(id: number, currency_code: string): Promise<IntegrationStatics> {
  const params = `?currency_code=${currency_code}`
  const url = `/api/v1/user_integrations/${id}/get_integration_statics/`
  const { data } = await axios.get(`${url}${params}`)
  return data
}

export async function getIntegrationProjectsListAPI(
  id: number, currency_code: string
): Promise<IntegrationProjects[]> {
  const params = `?currency_code=${currency_code}`
  const url = `/api/v1/user_integrations/${id}/get_projects/`
  const { data } = await axios.get(`${url}${params}`)
  return data.data
}

export async function getAllUserIntegrationsToShareForAccount(integration_id: number,project_id: number):
Promise<MyIntegrationsType[]> {
  const url = `/api/v1/user_integrations/get_all_user_integrations_to_share_for_account/`
  const { data } = await axios.get(url, {
    params: {
      integration_id: integration_id,
      project_id: project_id
    }
  })
  return data.data
}

export async function addProjectToIntegrations(project_id: number, integration_code: string): Promise<{ data: any }> {
  const { data } = await axios.post(`/api/v1/widget/add_project/`, {
    'project': project_id,
    'integration_code': integration_code
  })
  return data.data
}

export async function deactivateIntegration(integration_id: number,is_active: boolean): Promise<any> {
  const url = `/api/v1/user_integrations/${integration_id}/deactivate_integration/`
  const { data } = await axios.get(url, {
    params: {
      is_active: is_active
    }
  })
  return data.data
}

export async function reactivateIntegrationAPI(integration_code: string): Promise<any> {
  const params = `?integration_code=${integration_code}&is_active=True`
  const url = `/api/v1/user_integrations/activate_integration/${params}`
  const { data } = await axios.patch(url, {})
  return data
}

export async function updateLogoAPI(id: any, payload: any): Promise<any> {
  const url = `/api/v1/user_integrations/${id}/update_integration_logo/`
  const { data } = await axios.put(url, payload)
  return data
}

export async function validateOffsetFromFileAPI(array: any[], integrationToken: string): Promise<any> {
  const url = '/api/v1/widget/validate_offset_transactions_from_file/'
  const { data } = await axios.post(url, array, { headers: { 'Authorization': 'Token ' + integrationToken } })
  return data
}

export async function offsetPurchaseAPI(array: any[], integrationToken: string): Promise<any> {
  const url = '/api/v1/offsets/'
  const response = await axios.post(url, array, { headers: { 'Authorization': 'Token ' + integrationToken } })
  return response
}

export async function getProjectsIntegrationsAPI(): Promise<SmallProjectIntegration[]> {
  const url = '/api/v1/user_integrations/get_all_integration_projects/'
  const { data } = await axios.get(url)
  return data?.data
}

export async function getCreditCardOrdersAPI(orderId: number): Promise<CreditCardOrders> {
  const url = `/api/v1/credit_card_orders/${orderId}`
  const { data } = await axios.get<CreditCardOrders>(url)
  return data
}

export async function prePurchaseBuyNowAPI(
  integration_code: string | string[], payment_method: string,
  pre_purchased: any, deposit: any
): Promise<PrePurchaseBuyNow> {
  const { data } = await axios.post(
    '/api/v1/widget/pre_purchase/?integration_code=' +
    integration_code,
    {
      payment_method,
      pre_purchased,
      deposit
    }
  )
  return data
}

export async function finalizePaypalPaymentAPI(payer_id: number, payment_id: number): Promise<any> {
  return axios.post('/api/v1/deposits/finalize_paypal_payment/', { payer_id, payment_id })
}

export async function getIntegrationOptionalFieldsAPI(integrationId: number): Promise<OffsetOptionalFieldsType> {
  const url = `/api/v1/user_integrations/${integrationId}/get_integration_offset_optional_fields/`
  const { data } = await axios.get<OffsetOptionalFieldsType>(url)
  return data
}

export async function getIntegrationWhiteLabelAPI(integrationId: number): Promise<SmallWhiteLabel> {
  const url = `/api/v1/user_integrations/${integrationId}/get_integration_whitelabel_details/`
  const { data } = await axios.get<SmallWhiteLabel>(url)
  return data
}

export async function updateIntegrationWhiteLabelAPI(
  integrationId: number, payload: object
): Promise<string> {
  const url = `/api/v1/user_integrations/${integrationId}/update_user_whitelabel/`
  const { data } = await axios.patch(url, payload)
  return data
}

export async function recalculateIntegrationPurchasesAPI(initials: string): Promise<string> {
  const url = `/api/v1/user_integrations/recalculate_transactions/?initials=${initials}`
  const { data } = await axios.get(url)
  return data
}
export async function fetchFinalPrice(params)
: Promise<FinalUnitPrice> {
  const response = await axios.get('/api/v1/final_price/', { params })
  return response.data
}
